import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Typography,
  Button,
  TextField,
  Modal,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import api, { API_BASE_URL } from "../../src/utils/api";

const ViolationComments = ({ violation_id, currentUserId, event_id = null }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentType, setCommentType] = useState("external");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [imageURL, setImageURL] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);

  useEffect(() => {
    if (!violation_id) return;

    const fetchComments = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(
          `/getViolationComments/${violation_id}`
        );
        setComments(response.data);
      } catch (error) {
        console.error("Failed to fetch comments:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComments();
  }, [violation_id]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setFilePreview(URL.createObjectURL(file)); // No need for type check now
    }
  }, []);
  
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/*": [], 
    },
    multiple: false,
  });
  

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert("Comment cannot be empty.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("violation_id", violation_id);
      formData.append("user_id", currentUserId);
      formData.append("comment_text", newComment);
      formData.append("is_internal", commentType === "internal" ? 1 : 0);
      formData.append("event_id", event_id || "");

      if (selectedFile) {
        formData.append("upload_urls", selectedFile);
      }

      await api.post(`/addViolationComment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setComments((prevComments) => [
        {
          comment_text: newComment,
          sent_at: new Date().toISOString(),
          user_name: "You",
          user_lastname: "",
          is_internal: commentType === "internal",
          upload_urls: selectedFile ? selectedFile.name : null,
        },
        ...prevComments,
      ]);

      setNewComment("");
      setCommentType("external");
      setSelectedFile(null);
      setFilePreview(null);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to add comment:", error);
      alert("Failed to add comment. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Comments
      </Typography>

      {comments.length > 0 ? (
        comments.map((comment, index) => (
          <Box
            key={index}
            sx={{
              mb: 2,
              p: 2,
              border: "1px solid #ddd",
              borderRadius: "8px",
              backgroundColor: comment.is_internal ? "#fbe9e7" : "#e3f2fd",
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: "bold", color: comment.is_internal ? "red" : "blue" }}
            >
              {comment.is_internal ? "Internal Comment" : "External Comment"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {comment.comment_text}
            </Typography>

            {comment.upload_urls && (
  <Box sx={{ mt: 1 }}>
    <Typography variant="body2" fontWeight="bold">📎 Attached Image:</Typography>
    {(() => {
      const filenameOnly = comment.upload_urls?.split(/(\\|\/)/g).pop();
      const fileUrl = `${API_BASE_URL}/violationsimages/${encodeURIComponent(filenameOnly)}`;

      return (
        <Card
          elevation={2}
          sx={{ p: 1, mt: 1, cursor: "pointer", maxWidth: 200 }}
          onClick={() => {
            setImageURL(fileUrl);
            setOpenImageModal(true);
          }}
        >
          <img
            src={fileUrl}
            alt="Attachment"
            style={{ width: "100%", borderRadius: "8px" }}
          />
        </Card>
      );
    })()}
  </Box>
)}







            <Typography variant="caption" color="textSecondary">
              Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
              {new Date(comment.sent_at).toLocaleString()}
            </Typography>
          </Box>
        ))
      ) : (
        <Typography>No comments available.</Typography>
      )}

      <Button variant="contained" color="primary" onClick={() => setIsModalOpen(true)}>
        Add Comment
      </Button>

      {/* Add Comment Modal */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} aria-labelledby="add-comment-modal">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            padding: 4,
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Typography id="add-comment-modal" variant="h6" sx={{ mb: 2 }}>
            Add Comment
          </Typography>

          <TextField
            fullWidth
            label="Comment"
            multiline
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Comment Type</InputLabel>
            <Select value={commentType} onChange={(e) => setCommentType(e.target.value)}>
              <MenuItem value="external">External</MenuItem>
              <MenuItem value="internal">Internal</MenuItem>
            </Select>
          </FormControl>

          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed #ddd",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: "#f9f9f9",
              mb: 2,
            }}
          >
            <input {...getInputProps()} />
            <Typography>Drag & Drop a file here, or click to select one</Typography>
          </Box>

          {filePreview && (
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2">📎 File Preview:</Typography>
              <img src={filePreview} alt="Preview" style={{ maxWidth: "100%", maxHeight: 200 }} />
            </Box>
          )}

          <Button fullWidth variant="contained" color="primary" onClick={handleAddComment} sx={{ mt: 2 }}>
            Save Comment
          </Button>
        </Box>
      </Modal>

      {/* Image Modal */}
      <Dialog open={openImageModal} onClose={() => setOpenImageModal(false)} maxWidth="md">
  <DialogContent sx={{ position: "relative", textAlign: "center" }}>
    <IconButton
      onClick={() => setOpenImageModal(false)}
      sx={{ position: "absolute", top: 8, right: 8 }}
    >
      <CloseIcon />
    </IconButton>
    <img
      src={imageURL}
      alt="Full View"
      style={{ width: "100%", maxHeight: "90vh", objectFit: "contain" }}
    />
  </DialogContent>
</Dialog>

    </Box>
  );
};

export default ViolationComments;
